import { useEffect, useRef, useState } from 'react';

import ErrorMessage from '~/common/componentMapper/PdfViewer/ErrorMessage';
import Box from '~/common/components/Box';
import { TOP_NAV_HEIGHT } from '~/common/components/GlobalLayout/PageLayout/Styled';
import Spinner from '~/common/components/Spinner';

import Area from './Area';
import Toolbar from './Toolbar';
import { defaultScale, minScale } from './constants';
import type {
  Citation,
  DocumentOption,
  Highlight,
  PageRotation,
} from './types';

type ViewerProps = {
  docname?: string;
  documentId?: number;
  pdf?: string | null;
  highlights?: Array<Highlight>;
  pageRotations?: Array<PageRotation>;
  pageNumber?: number;
  setPageNumber?: (pageNumber: number) => unknown;
  citations?: Array<Citation['boundingBoxes'][number]>;
  isFullPage?: boolean;
  documentOptions?: Array<DocumentOption>;
  showHighLight?: boolean;
};

function getAvailablePageNumber(pageNumber: number, numPages: number) {
  return numPages > 0 ? Math.min(pageNumber, numPages) : pageNumber;
}

function Viewer({
  docname,
  documentId,
  pdf,
  highlights,
  pageRotations,
  pageNumber,
  citations,
  isFullPage,
  documentOptions,
  showHighLight,
}: Readonly<ViewerProps>) {
  const [numPages, setNumPages] = useState(0);
  const [isLoadComplete, setIsLoadComplete] = useState(true);
  const [display, setDisplay] = useState('none');
  const [scale, setScale] = useState(defaultScale);
  const [zoom, setZoom] = useState(() => convertScaleToZoom(scale));
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(
    pageNumber ?? 0,
  );
  const pendingPageNumberRef = useRef(pageNumber ?? 0);

  useEffect(() => {
    if (pdf === null) {
      setNumPages(0);
      setCurrentPageNumber(pageNumber ?? 0);
      pendingPageNumberRef.current = pageNumber ?? 0;
      setIsLoadComplete(true);
    } else {
      setIsLoadComplete(false);
      setScale(defaultScale);
      pendingPageNumberRef.current = pageNumber ?? 0;
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [pdf]);

  useEffect(() => {
    const availablePageNumber = getAvailablePageNumber(
      pageNumber ?? 0,
      numPages,
    );
    setCurrentPageNumber(availablePageNumber);
    pendingPageNumberRef.current = availablePageNumber;
  }, [numPages, pageNumber]);

  useEffect(() => {
    if (isLoadComplete) {
      setDisplay('flex');
      observePageVisibility();
    } else {
      setDisplay('none');
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [isLoadComplete]);

  useEffect(() => {
    if (!citations) return;

    if (citations[0]?.pageNumber) {
      setCurrentPageNumber(citations[0].pageNumber);
      pendingPageNumberRef.current = citations[0].pageNumber;
    }
  }, [citations]);

  useEffect(() => {
    setZoom(convertScaleToZoom(scale));
  }, [scale]);

  function convertScaleToZoom(scale) {
    const percent = scale * 100;
    const roundedPercent = Math.ceil(percent / 5) * 5;
    return roundedPercent;
  }

  function handleUserScaleChange(newScale: number) {
    // User manually changed zoom via toolbar
    if (newScale > minScale) {
      setScale(newScale);
      observePageVisibility();
    }
  }

  function handleDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
  }
  function handleRenderSuccess({ pageNumber: renderedPageNumber }) {
    const requestedPageNumber = currentPageNumber || 1;
    if (renderedPageNumber === requestedPageNumber) {
      setIsLoadComplete(true);
    }
  }

  function handleDocumentError(error) {
    const errorString = error.toString();
    if (errorString.includes('Worker was destroyed')) return;
    setIsLoadComplete(true);
  }

  function navigateToPage(pageNumber: number) {
    pendingPageNumberRef.current = pageNumber;
    setCurrentPageNumber(pageNumber);
  }

  function observePageVisibility() {
    const options = {
      root: document.querySelector('#viewerArea'),
      rootMargin: '0px',
      threshold: 0,
    };
    const visiblePages = new Set<number>();

    const callback = (entries) => {
      entries.forEach((entry) => {
        const pageNum = Number(entry.target.dataset.pageNumber);
        if (entry.isIntersecting) {
          visiblePages.add(pageNum);
        } else {
          visiblePages.delete(pageNum);
        }
      });

      const pendingPageNumber = pendingPageNumberRef.current;
      if (pendingPageNumber > 0) {
        if (visiblePages.has(pendingPageNumber)) {
          pendingPageNumberRef.current = 0;
        }
        return;
      }

      const minPage = Math.min(...visiblePages);
      if (visiblePages.size > 0) {
        setCurrentPageNumber(minPage);
      }
    };
    const observer = new IntersectionObserver(callback, options);
    for (let i = 1; i <= numPages; i++) {
      const selector = `.react-pdf__Page[data-page-number="${i}"]`;
      const target = document.querySelector(selector);
      if (target !== null) {
        observer.observe(target);
      }
    }
  }

  return (
    <Box
      id="viewerArea"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        background: '#1E1E1E',
        height: `calc(100vh - ${TOP_NAV_HEIGHT} - var(--gl-header-height, 0px))`,
      }}
    >
      <Toolbar
        docName={docname}
        documentId={documentId}
        currentPageNumber={pdf === undefined ? 0 : currentPageNumber}
        setPageNumberChange={navigateToPage}
        totalPages={numPages}
        minZoom={minScale * 100}
        scale={scale}
        zoom={zoom}
        onScaleChange={handleUserScaleChange}
        sx={{ display: { display } }}
        documentOptions={documentOptions}
      />
      <Area
        pdf={pdf}
        scale={scale}
        display={display}
        onRenderSuccess={handleRenderSuccess}
        onLoadSuccess={handleDocumentLoadSuccess}
        onLoadError={handleDocumentError}
        highlights={highlights}
        citations={citations}
        pageNumber={currentPageNumber}
        pageRotations={pageRotations}
        isFullPage={isFullPage}
        showHighLight={showHighLight}
      />
      {!isLoadComplete && pdf && <Spinner />}
      {(pdf === undefined || pdf === 'noDocumentFound') && (
        <ErrorMessage message={'No Document(s) Available'} />
      )}
    </Box>
  );
}

export default Viewer;
